import * as et from "../application/EventTypes";
import { GlobalManagerProvider } from '../application/GlobalManagerMixin';
import { Prefs3D } from '../application/PreferenceNames';

// /** @constructor */
//
// TODO: Pass in the api instead of the impl, don't use the impl object.
//

const _keys = {
  SHIFT: 16,
  CONTROL: 17,
  ALT: 18,
  SPACE: 32,
  PAGEUP: 33,
  PAGEDOWN: 34,
  LEFT: 37,
  UP: 38,
  RIGHT: 39,
  DOWN: 40,
  ZERO: 48,
  EQUALS: 187,
  DASH: 189
};

// Interaction Triggers:
const kNone = -5;
const kKeyboard = -4;
const kWheel = -1;
const kMouseLeft = 0;
const kMouseMiddle = 1;
const kMouseRight = 2;
const kTouch = 3;

export class OrbitDollyPanTool extends GlobalManagerProvider {
  // Simple class fields for properties that don't need constructor logic
  active = false;

  _kScreenEpsilon = 0.001;
  _kEpsilon = 0.01;
  _kAutoDeltaZ = 1.5; // Dolly increment
  _kAutoDeltaXY = 0.01;
  _kAutoScreenXY = 20;
  _kDollyPinchScale = 0.5;
  _kOrbitScale = 2.0;

  _touchType = null;
  _pinchScale = 1.0;
  _prevPinchScale = 1.0;
  _prevPinchLength = 0;
  _pinchLength = 0;
  _deltaRoll = 0.0;
  _prevRoll = 0.0;

  _useCustomCursors = true;
  _activeModeLocked = false;
  _autoCamStartXY = null;
  _interactionActive = false;
  _lastMouseX = undefined;
  _lastMouseY = undefined;

  _activeTrigger = kNone;
  _startXYZ = new THREE.Vector3();
  _moveXYZ = new THREE.Vector3();
  _touchStartXY = new THREE.Vector2();
  _startXY = new THREE.Vector2();
  _moveXY = new THREE.Vector2();
  _deltaXY = new THREE.Vector2();
  _motionDelta = new THREE.Vector3();

  _rotateStart = new THREE.Vector3();
  _rotateEnd = new THREE.Vector3();
  _pivotToEye = new THREE.Vector3();
  _targetToEye = new THREE.Vector3();
  _projVector = new THREE.Vector3();
  _objectUp = new THREE.Vector3();
  _mouseOnBall = new THREE.Vector3();
  _rotateNormal = new THREE.Vector3();
  _quaternion = new THREE.Quaternion();
  _intersectionPlane = new THREE.Plane();
  _noRoll = false;
  _staticMoving = true;
  _dynamicDampingFactor = 0.2;

  _autoMove = [false, false, false, false, false, false]; // left, right, up, down, in, out
  _modifierState = { SHIFT: 0, ALT: 0, CONTROL: 0, SPACE: 0 };

  _kDampingFactor = 0.6;
  _kLookSpeedDefault = 5.0;
  _kDollySpeedDefault = 0.025;
  _kMinDollySpeed = 0.01;
  _kClosestDistance = 0.0005;

  _trackingDistance = 1.0;
  _boundingBox = undefined;

  _names = ["orbit", "freeorbit", "dolly", "pan"];

  constructor(viewerImpl, viewerApi, config) {
    super();
    this._viewerImpl = viewerImpl;
    this._viewerApi = viewerApi;
    this._config = config;

    this.setGlobalManager(this._viewerApi.globalManager);

    this._kDollyDragScale = this._config && Object.prototype.hasOwnProperty.call(this._config, 'dollyDragScale') ? this._config.dollyDragScale : 100.0;

    this._isMac = navigator.userAgent.search("Mac OS") != -1;
    this._isPinchRotationDisabled = this._config && this._config.disablePinchRotation;

    this._navapi = this._viewerApi.navigation;
    this._camera = this._navapi.getCamera();

    this._activeMode = this._names[0];
    this._activations = [this._activeMode]; // Safeguard
    this._activatedMode = this._activeMode;

    // Remove rollover effect when mouse is not over the canvas.
    this._onMouseOut = (event) => {
      this._viewerImpl.renderer().rolloverObjectId(0, null, 0);
    };

    this._kDollyScale = this._config && Object.prototype.hasOwnProperty.call(this._config, 'dollyScrollScale') ? this._config.dollyScrollScale : 0.6;

    this._myLookSpeed = this._kLookSpeedDefault;
    this._myDollySpeed = this._kDollySpeedDefault;

    // private
    this._onPrefCursor = (value) => {
      this._useCustomCursors = value;
    };
  }

  getNames()
  {
    return this._names;
  }

  getName()
  {
    return this._names[0];
  }

  activate(name)
  {
    this.active = true;
    // avp.logger.log("ACTIVATE: " + _activatedMode + " => " + name);
    this._activations.push(name);
    this._activatedMode = name;

    this._useCustomCursors = this._viewerApi.prefs.get(Prefs3D.ENABLE_CUSTOM_ORBIT_TOOL_CURSOR);
    this._viewerApi.prefs.addListeners(Prefs3D.ENABLE_CUSTOM_ORBIT_TOOL_CURSOR, this._onPrefCursor);

    this._viewerImpl.canvas.addEventListener('mouseout', this._onMouseOut);
    this._viewerApi.addEventListener(et.ESCAPE_EVENT, this.handleBlur.bind(this));
  }

  deactivate(name)
  {
    this.active = false;

    this._viewerApi.prefs.removeListeners(Prefs3D.ENABLE_CUSTOM_ORBIT_TOOL_CURSOR, this._onPrefCursor);

    var end = this._activations.length - 1;
    if (end > 0 && this._activations[end] === name)
    {
      this._activations.pop();
      this._activatedMode = this._activations[end - 1];

      // avp.logger.log("DEACTIVATE: " + name + " => " + _activatedMode );
    }
    this._viewerImpl.canvas.removeEventListener('mouseout', this._onMouseOut);
    this._viewerApi.removeEventListener(et.ESCAPE_EVENT, this.handleBlur.bind(this));
  }

  isActive() {
    return this.active;
  }

  adjustDollyLookSpeed(direction)
  {
    if (direction === 0)
    {
      this._myDollySpeed = this._kDollySpeedDefault;
      this._myLookSpeed = this._kLookSpeedDefault;
    } else

    {
      this._myDollySpeed *= direction > 0 ? 1.10 : 0.90;
      this._myLookSpeed *= direction > 0 ? 1.10 : 0.90;

      // May need more appropriate minimums (and maximums) here.
      if (this._myDollySpeed < 0.000001)
      this._myDollySpeed = 0.000001;

      if (this._myLookSpeed < 0.000001)
      this._myLookSpeed = 0.000001;
    }
  }

  getDollySpeed(dollyTarget, isZoomIn)
  {
    // Calculate the distance that one unit of virtual dolly will move:
    var speed;
    // In order to avoid bugs as described in BLMV-3936, when the zoom is not working properlly in orthoCamera when zooming towards pivot,
    // We want to compute the speed by orthoScale (which considers the "distance" between the camera and it's target)
    if ((this._navapi.getZoomTowardsPivot() || this._navapi.getWheelSetsPivot()) && !this._camera.isPerspective && !this._navapi.getIs2D()) {
      let distance = this._camera.orthoScale * this._myDollySpeed;
      speed = Math.abs(distance) < this._kClosestDistance && isZoomIn ? 0 : distance;
    } else {
      var view = this._navapi.getEyeVector();
      var position = this._navapi.getPosition();
      var projectedLength = dollyTarget.clone().sub(position).dot(view.normalize());
      let distance = projectedLength * this._myDollySpeed;
      speed = Math.abs(distance) < this._kMinDollySpeed ? distance < 0 ? -this._kMinDollySpeed : this._kMinDollySpeed : distance;
    }

    return speed;
  }

  setPivotPointFromInput(inputX, inputY) {

    if (this._navapi.getIs2D() || !this._navapi.getWheelSetsPivot()) {
      return;
    }

    var res = this._viewerImpl.hitTest(inputX, inputY, true);
    if (res && res.intersectPoint) {
      this.utilities.setPivotPoint(res.intersectPoint, true, true);
    } else {
      // When we don't get an intersection with the model, compute the hitPoint with a plane that is:
      // Parallel to the current near/far-plane
      // At the same distance as the previous pivot.
      const pivot = this._navapi.getPivotPoint();
      const eye = this._navapi.getEyeVector().normalize();
      const dist = -pivot.dot(eye);
      this._intersectionPlane.set(eye, dist);

      const ray = this._viewerImpl.viewportToRay(this._viewerImpl.clientToViewport(inputX, inputY));
      let pos = new THREE.Vector3();
      ray.intersectPlane(this._intersectionPlane, pos);
      this.utilities.setPivotPoint(pos, true, true);
    }
  }

  /**
   * Get the dolly (zoom) scale value which is used for the scroll wheel.
   * @returns {Number} - dolly scroll scale
   */
  getDollyScrollScale()
  {
    return this._kDollyScale;
  }

  /**
   * Set the dolly (zoom) scale of the scroll wheel.
   * This value is used to calculate the distance of the camera.
   * The default value is 0.6.
   *
   * @example
   * // Zoom at a larger scale
   * viewer.toolController.getTool("dolly").setDollyScrollScale(2);
   *
   * // Zoom at a lower scale
   * viewer.toolController.getTool("dolly").setDollyScrollScale(0.1);
   *
   * @param {Number} dollyScale
   */
  setDollyScrollScale(dollyScale)
  {
    this._kDollyScale = dollyScale;
  }

  /**
   * Get the dolly (zoom) scale value which is used after enabling the zoom tool and dragging the mouse.
   * @returns {Number} - dolly drag scale
   */
  getDollyDragScale()
  {
    return this._kDollyDragScale;
  }

  /**
   * Set the dolly (zoom) scale of the zoom tool.
   * This value is used to calculate the distance of the camera.
   * The default value is 100.
   *
   * @example
   * // Zoom at a larger scale
   * viewer.toolController.getTool("dolly").setDollyDragScale(50);
   *
   * // Zoom at a lower scale
   * viewer.toolController.getTool("dolly").setDollyDragScale(200);
   *
   * @param {Number} dollyScale
   */
  setDollyDragScale(dollyDragScale)
  {
    this._kDollyDragScale = dollyDragScale;
  }

  getLookSpeed()
  {
    return this._myLookSpeed;
  }

  coiIsActive()
  {
    return this._navapi.getPivotSetFlag() && this._navapi.isPointVisible(this._navapi.getPivotPoint());
  }

  adjustSpeed(direction)
  {
    this.adjustDollyLookSpeed(direction);

    if (this.utilities.autocam)
    this.utilities.autocam.orbitMultiplier = this.getLookSpeed();
  }

  getTriggeredMode()
  {
    // Fusion wants Shift+Middle to go back to orbit
    if (this._activeTrigger === kMouseMiddle && this._modifierState.SHIFT)
    return this._activations[1]; // TODO_NOP: return to chosen orbit behavior, don't use _activations

    return this._isDolly() || this._motionDelta.z !== 0.0 ? this._shouldPanOverrideDolly() ? "pan" : "dolly" :
    this._isTrack() ? "pan" :
    this._touchType === 'pan' || this._touchType === 'pinch' ? "dollypan" :
    this._activatedMode;
  }

  // TO DO: Where/when do we push/pop tool state?
  _isTrack() {
    var mod = this._modifierState;
    return this._activeTrigger === kMouseRight && !mod.SHIFT && !(mod.ALT ^ mod.CONTROL) ||
    this._activeTrigger === kMouseRight && mod.SHIFT && mod.CONTROL ||
    this._activeTrigger === kMouseMiddle && !mod.SHIFT && !mod.CONTROL ||
    this._activeTrigger === kMouseMiddle && mod.ALT ||
    this._activeTrigger === kMouseMiddle && mod.CONTROL && !mod.ALT ||
    this._activeTrigger === kMouseLeft && mod.SHIFT && !mod.CONTROL && !mod.ALT ||
    this._activatedMode === "pan" && this._activeTrigger !== kMouseMiddle && !mod.ALT && !(this._touchType === "pinch") ||
    mod.SPACE;
  }

  _isDolly() {
    var mod = this._modifierState;
    return this._activeTrigger === kMouseRight && mod.SHIFT && !mod.ALT && !mod.CONTROL ||
    this._activeTrigger === kMouseRight && mod.ALT && !mod.SHIFT && !mod.CONTROL ||
    this._activatedMode === "dolly" && !mod.ALT && !(this._touchType === "pinch");
  }

  _shouldPanOverrideDolly() {
    var mod = this._modifierState;
    return !mod.CONTROL && !mod.ALT && !mod.SHIFT && (this._activeTrigger === kMouseRight || this._activeTrigger === kMouseMiddle);
  }

  initTracking(x, y)
  {
    var distance;

    if (!this._camera.isPerspective)
    {
      distance = this._navapi.getEyeVector().length();
    } else

    {
      // Decide what point in world space defines the plane
      // orthogonal to the view that will be used to track
      // the camera. If we get an intersection point use it,
      // otherwise if the pivot point is set use that. The
      // fallback is to use the mid-point of the view frustum.

      distance = (this._camera.near + this._camera.far) * 0.5;

      var p = this.utilities.getHitPoint(x, y);
      var position = this._navapi.getPosition();
      if (p && p.sub)
      {
        // Calculate orthogonal distance along view vector:
        var hitToEye = p.sub(position);
        var view = this._navapi.getEyeVector().normalize();
        distance = Math.abs(view.dot(hitToEye));
      } else

      {
        var usePivot = this._navapi.getPivotSetFlag() && this._navapi.isPointVisible(this._navapi.getPivotPoint());
        if (usePivot)
        {
          var pivotDistance = this._navapi.getPivotPlaneDistance();
          if (pivotDistance > this._kEpsilon)
          {
            distance = pivotDistance;
          }
        }
      }
    }
    this._trackingDistance = distance;
  }

  _pivotIsBehind() {
    if (!this._camera.isPerspective)
    return false;


    var pivotVector = this._navapi.getPivotPoint().sub(this._navapi.getPosition());
    return pivotVector.dot(this._navapi.getEyeVector()) <= 0.0;
  }

  initOrbit()
  {
    // If the pivot point is behind us we pivot around the center of the view:
    this.utilities.setTemporaryPivot(this._pivotIsBehind() ? this._navapi.getTarget() : null);
  }

  getActiveMode()
  {
    return this._activeMode;
  }

  getCursor()
  {
    if (!this._useCustomCursors)
    return null;

    switch (this._activeMode) {

      case "freeorbit":
      case "orbit":
        return 'url(data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABgAAAAYCAMAAADXqc3KAAAAt1BMVEUAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAD///8AAAAzMzP6+vri4uISEhKKioqtra2dnZ2EhIR9fX10dHRkZGQdHR3t7e3Hx8e5ubm1tbWoqKhWVlZKSko4ODgICAjv7+/o6OjMzMyxsbFOTk4pKSkXFxcEBAT29vbW1tZ6enpISEgLCwvhzeX+AAAAGXRSTlMANRO0nHRJHfnskIxQRKh89syDVwTWZjEJxPFEswAAAOFJREFUKM+1j+lygkAQhIflEAJe0Rw9u4CCeKKoSTTX+z9XoMJWWeX+ssrvZ3f19DQ5zOw/0DUMQPlmQ72bE2adBp8/Rp3CQUi3ILx+bxj4fjDs9T1Bmo6bbPPN8aDU4bjJt4nb+de789kSFyxn826jW3ICLNZZKU8nWWbrBTCRVm04U8TpjquRFf1Go0d7l8aYOrUR7FGEFr1S9LGymwthgX2gE/Kl0cHPOtF2xOWZ5QpIC93RflW4InkDoPRXesd5LJIMQPzV7tCMa7f6BvhJL79AVDmYTNQ1NhnxbI/uwB8H5Bjd4zQPBAAAAABJRU5ErkJggg==), auto';

      case "dolly":
        return "url(data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABgAAAAYCAMAAADXqc3KAAAAgVBMVEUAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAD///8mJiYAAADNzc2/v7+fn59paWlPT08MDAwICAj6+vqpqak7Ozv29vby8vLp6em2traAgIBkZGRZWVlAQEAaGhpISEgkS7tbAAAAFHRSTlMAOvhpZD8mkQWegMy9qY1YVE01EYiqlE0AAADZSURBVCjPbY9ZloMgEAAbEbfsmRZZXbJn7n/AAX2RQVN/VD26AXLOeZLDGo6IbfI9tHq8cdxuj1HwvgCoaiHqKoRk+M3hB9jueUW8PnfsE/bJ3vms7nCkq7NoE3s99AXxoh8vFoXCpknrn5faAuJCenT0xPkYqnxQFJaU0gdZrsKm8aHZrAIffBj40mc1jsTfIJRWegq6opTMvlfqLqYg7kr1ZB7jFgeaMC59N//8O4WZ1IiPF8b5wMHcJn8zB4g4mc77zpxgAbMSUVoGK4iV0hL4wrksz+H0Bw5+E+HrniDQAAAAAElFTkSuQmCC), auto";

      case "pan":
        return "url(data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABgAAAAYCAMAAADXqc3KAAABHVBMVEUAAABPTk4AAAAAAAAJCQkRERE0MzQQEBAODg4QEBB4d3dbWlo9PDw/Pj4vLy8sLCwZGBgWFhYcHBwKCgoSEhIAAAAKCgoICAgKCgoQEBAODg4EBAQICAgPDw8REREMDAx2dnY0NDQvLy9QUFAaGhomJSYjIyM7OjokJCQNDA0mJiYNDQ0AAAAUFBQJCQkQEBAEBAQNDQ0PDw8VFRX///+amJkAAAD5+fnz8/PKycn9/f339vbi4eLR0dDNzMyAgIB8e3xycHH7+/vw7+/o6OjX1ta7urq4t7iwsLCnp6eioqKbmppva21OTk74+Pjl5eXc3Nzb29vLy8vDw8PDwsKrqqqdnZ2WlpaSkpKTkZKMiouEg4NkZGRISEgxLzBpgbsEAAAANHRSTlMA+fiQXgngKSYG/vX17uvBuqackpCNg3BpUkpAPBwTDvj18+vl0s/NwrOwoZZ+TDg4NBkBGrzX8QAAAP5JREFUKM99j9Vuw0AQRdeuKZyGkyZNmbnXDLHDVGb8/8/oy7paK1bO0+oc7WiGnGiaxq+QRTQAOh8f9Jv4H/Ge8PZPrCdlvkxfYluUT2WyyCq3mZ7unwlKVLcqOzA/Mf71j0TWJ/Ym6rPeca05Ni4iIevYc7yoUD2zQFhq71BdI9nvBeBabFDSPe8DswlUc1Riw3VxbH0NHBUPQ0jrbDnPYDjALQBMq9E7nkC5y7VDKTZlUg8Q0lmjvl74zlYErgvKa42GPKf3/a0kQmYCDY1SYMDosqMoiWrGwz/uAbNvc/fNon4kXRKGq+PUo2Mb96afV0iUxqGU2s4VBbKUP65NL/LKF+7ZAAAAAElFTkSuQmCC), auto";
    }
    return null;
  }

  getMotionDelta(dxyz, dxy)
  {
    // Add any offset triggered by key controls:
    // TODO: Change these to scale based on real time so fast frame
    // rate doesn't cause super fast motion.

    var autoDeltaZ = this._navapi && this._navapi.getReverseZoomDirection() ? -this._kAutoDeltaZ : this._kAutoDeltaZ;
    if (this._isMac)
    autoDeltaZ *= -1; // Match the "natural" scroll direction on Mac.

    if (this._autoMove[0]) {this._moveXYZ.x += this._kAutoDeltaXY;this._moveXY.x += this._kAutoScreenXY;}
    if (this._autoMove[1]) {this._moveXYZ.x -= this._kAutoDeltaXY;this._moveXY.x -= this._kAutoScreenXY;}
    if (this._autoMove[2]) {this._moveXYZ.y += this._kAutoDeltaXY;this._moveXY.y += this._kAutoScreenXY;}
    if (this._autoMove[3]) {this._moveXYZ.y -= this._kAutoDeltaXY;this._moveXY.y -= this._kAutoScreenXY;}
    if (this._autoMove[4]) {this._moveXYZ.z += autoDeltaZ;}
    if (this._autoMove[5]) {this._moveXYZ.z -= autoDeltaZ;}

    var deltaX = this._moveXYZ.x - this._startXYZ.x;
    var deltaY = this._moveXYZ.y - this._startXYZ.y;
    var deltaZ = this._moveXYZ.z - this._startXYZ.z;

    if (Math.abs(deltaX) < this._kScreenEpsilon) deltaX = 0.0;
    if (Math.abs(deltaY) < this._kScreenEpsilon) deltaY = 0.0;
    if (Math.abs(deltaZ) < this._kScreenEpsilon) deltaZ = 0.0;

    dxyz.set(deltaX, deltaY, deltaZ);

    if (dxy)
    {
      dxy.set(this._moveXY.x - this._startXY.x, this._moveXY.y - this._startXY.y);
    }
  }

  stepMotionDelta(delta, damped)
  {
    if (damped)
    {
      this._startXYZ.x += delta.x * this._kDampingFactor;
      this._startXYZ.y += delta.y * this._kDampingFactor;
      this._startXYZ.z += delta.z * this._kDampingFactor;
    } else

    this._startXYZ.copy(this._moveXYZ);

    this._startXY.copy(this._moveXY);
  }

  _getMouseProjectionOnBall(pageX, pageY) {
    var _window = this.getWindow();
    var viewport = { left: 0, top: 0, width: _window.innerWidth, height: _window.innerHeight };

    this._mouseOnBall.set(
      (pageX - viewport.width * 0.5 - viewport.left) / (viewport.width * 0.5),
      (viewport.height * 0.5 + viewport.top - pageY) / (viewport.height * 0.5),
      0.0
    );

    var length = this._mouseOnBall.length();
    if (this._noRoll)
    {
      if (length < Math.SQRT1_2)
      {
        this._mouseOnBall.z = Math.sqrt(1.0 - length * length);
      } else

      {
        this._mouseOnBall.z = .5 / length;
      }
    } else
    if (length > 1.0)
    {
      this._mouseOnBall.normalize();
    } else

    {
      this._mouseOnBall.z = Math.sqrt(1.0 - length * length);
    }
    this._pivotToEye.copy(this._camera.position).sub(this._camera.pivot);
    this._projVector.copy(this._camera.up).setLength(this._mouseOnBall.y);
    this._projVector.add(this._objectUp.copy(this._camera.up).cross(this._pivotToEye).setLength(this._mouseOnBall.x));
    this._projVector.add(this._pivotToEye.setLength(this._mouseOnBall.z));
    return this._projVector;
  }

  _freeOrbit() {
    if (!this._navapi.isActionEnabled('orbit')) {
      return;
    }

    this._pivotToEye.subVectors(this._camera.position, this._camera.pivot);
    this._targetToEye.subVectors(this._camera.position, this._camera.target);
    var targetDist = this._targetToEye.length();
    this._targetToEye.normalize();

    var angle = Math.acos(this._rotateStart.dot(this._rotateEnd) / this._rotateStart.length() / this._rotateEnd.length());
    if (angle)
    {
      angle *= this._kOrbitScale;
      this._rotateNormal.crossVectors(this._rotateStart, this._rotateEnd).normalize();
      this._quaternion.setFromAxisAngle(this._rotateNormal, -angle);

      this._pivotToEye.applyQuaternion(this._quaternion);
      this._camera.up.applyQuaternion(this._quaternion);
      this._rotateEnd.applyQuaternion(this._quaternion);
      this._targetToEye.applyQuaternion(this._quaternion);

      if (this._staticMoving)
      {
        this._rotateStart.copy(this._rotateEnd);
      } else

      {
        this._quaternion.setFromAxisAngle(this._rotateNormal, angle * (this._dynamicDampingFactor - 1.0));
        this._rotateStart.applyQuaternion(this._quaternion);
      }
    }

    this._camera.position.addVectors(this._camera.pivot, this._pivotToEye);
    this._camera.target.subVectors(this._camera.position, this._targetToEye.multiplyScalar(targetDist));
    this._camera.dirty = true;
  }

  getFitBounds()
  {
    if (this.utilities.viewerImpl.zoomBoundsChanged) {
      this._boundingBox = this.utilities.viewerImpl.getVisibleBounds(true);
      this.utilities.viewerImpl.zoomBoundsChanged = false;
    }

    return this._boundingBox;
  }

  update()
  {
    var wheelEnded = false;
    var updatePivot = false;
    var viewport;

    this.getMotionDelta(this._motionDelta, this._deltaXY);

    var deltaX = this._motionDelta.x;
    var deltaY = this._motionDelta.y;
    var deltaZ = this._motionDelta.z;

    if (!this._activeModeLocked)
    this.checkInteractionMode();

    this._activeModeLocked = this._activeTrigger > kWheel;

    if (this._activeModeLocked)
    this.controller.setIsLocked(true);

    if (deltaX !== 0.0 || deltaY !== 0.0 || deltaZ !== 0.0)
    {
      switch (this._activeMode) {

        case "orbit":
          if (this.utilities.autocam && this.utilities.autocam.startState)
          {
            this._deltaXY.x = -this._deltaXY.x;
            if (this._autoCamStartXY)
            this.utilities.autocam.orbit(this._moveXY, this._autoCamStartXY, this._deltaXY.multiplyScalar(this._kOrbitScale), this.utilities.autocam.startState);
          }
          break;

        case "freeorbit":
          this._freeOrbit();
          break;

        case "dolly":
          var dollyTarget;
          var screen = new THREE.Vector2();

          deltaZ *= this._kDollyScale;

          if (this._activeTrigger >= kMouseLeft)
          {
            // Map XY movement to Z:
            deltaY = -deltaY; // Invert Y
            deltaZ = Math.abs(deltaX) > Math.abs(deltaY) ? deltaX : deltaY;

            if (this._navapi.getReverseZoomDirection()) {
              deltaZ *= -1;
            }

            deltaZ *= this._kDollyDragScale;
            deltaX = 0.0;
            deltaY = 0.0;

            // Towards center of viewport:
            screen = this._navapi.viewportToScreen(0, 0);
          } else

          {
            // Towards cursor position:
            viewport = this._navapi.getScreenViewport();
            if (this._lastMouseX && this._lastMouseY)
            {
              screen.x = this._lastMouseX / viewport.width;
              screen.y = this._lastMouseY / viewport.height;
            } else

            {
              screen = this._navapi.viewportToScreen(0, 0);
            }
          }
          if (!this._navapi.getIs2D() && this._navapi.getZoomTowardsPivot())
          {
            if (!this.coiIsActive())
            {
              // Center of viewport if pivot is not active
              screen = this._navapi.viewportToScreen(0, 0);
              dollyTarget = this._navapi.getWorldPoint(screen.x, screen.y);
            } else

            dollyTarget = this._navapi.getPivotPoint();
          } else

          dollyTarget = this._navapi.getWorldPoint(screen.x, screen.y);

          this._navapi.dollyFromPoint(deltaZ * this.getDollySpeed(dollyTarget, deltaZ < 0), dollyTarget, this.getFitBounds());
          break;

        case "pan":
          // Moving camera down/left moves the model up/right:
          this._navapi.panRelative(-deltaX, deltaY, this._trackingDistance);
          break;

        case "dollypan":

          if (deltaX !== 0.0 || deltaY !== 0.0)
          this._navapi.panRelative(-deltaX, deltaY, this._trackingDistance);

          // Towards cursor position:
          viewport = this._navapi.getScreenViewport();
          dollyTarget = this._navapi.getWorldPoint(this._lastMouseX / viewport.width, this._lastMouseY / viewport.height);

          var position = this._navapi.getPosition();
          var distance = this._navapi.getIs2D() ? position.sub(dollyTarget).length() : this._trackingDistance;
          var touchScale = this._prevPinchLength / this._pinchLength - 1;

          var distanceDelta = touchScale * distance;

          this._navapi.dollyFromPoint(distanceDelta, dollyTarget, this.getFitBounds());

          var vview = new THREE.Vector3();
          var qrotate = new THREE.Quaternion();

          var up = this._navapi.getCameraUpVector();
          var view = vview.copy(this._camera.position).sub(this._camera.target).normalize();
          qrotate.setFromAxisAngle(view, this._deltaRoll * 1.2);
          up.applyQuaternion(qrotate);

          if (!this._navapi.getIs2D() && !(this._isPinchRotationDisabled && (this._touchType === 'pinch' || this._touchType === 'pan')))
          this._navapi.setCameraUpVector(up);

          this._prevPinchLength = this._pinchLength;
          this._prevPinchScale = this._pinchScale;
          this._trackingDistance = distance + distanceDelta;
          break;
      }
      updatePivot = true;
    }
    this.stepMotionDelta(this._motionDelta, this._activeMode !== "pan" && this._activeMode !== 'dollypan');

    // If a wheel event triggered this we've now handled it,
    if (this._activeTrigger === kWheel && Math.abs(deltaZ) < this._kEpsilon)
    {
      this.interactionEnd(kWheel);

      //Clamp the motion vector to zero too, so that motion stops.
      //Otherwise zooming continues in tiny deltas that prevent
      //progressive repaint from starting.
      this._moveXYZ.z = 0;
      this._startXYZ.z = 0;

      wheelEnded = true;
      updatePivot = true;
    }

    // Show pivot if a clutch key is being held.
    if ((this._modifierState.SHIFT || this._modifierState.ALT) && this._names.indexOf(this._viewerApi.getActiveNavigationTool()) !== -1) {
      updatePivot = true;
    }

    // If the interaction has "ended" we can now forget the trigger.
    if (!this._interactionActive && (wheelEnded || this._activeTrigger > kNone))
    {
      if (this._activeTrigger > kWheel)
      {
        // Kill any ongoing damped motion if we aren't using
        // the wheel.
        this._startXYZ.copy(this._moveXYZ);

        this.utilities.removeTemporaryPivot();
      }
      this.utilities.autocam.endInteraction();
      this._activeTrigger = kNone;
      if (this._activeModeLocked)
      this.controller.setIsLocked(false);
      this._activeModeLocked = false;
      this._autoCamStartXY = null;
      this._touchType = null;
    }
    if (updatePivot)
    this.utilities.pivotActive(this._navapi.getPivotSetFlag(), this._activeTrigger <= kWheel);else

    this.utilities.pivotUpdate();

    return this._camera.dirty;
  }

  checkInteractionMode()
  {
    var newMode = this.getTriggeredMode();

    if (newMode !== this._activeMode)
    {
      this._activeMode = newMode;

      if (this._activeMode === "pan" && this._activeTrigger > kWheel || this._activeMode === "dollypan")
      this.initTracking(this._startXYZ.x, this._startXYZ.y);
    }
  }

  interactionStart(trigger, force)
  {
    // Just a simple way to give device input a sort of priority
    // so we don't have to track all active triggers. Just remember
    // the most recent with highest "priority".
    if (force || trigger > this._activeTrigger)
    {
      // Perhaps we need to remember the modifier keys now.
      this._activeTrigger = trigger;
      this._interactionActive = true;

      if (trigger > kWheel)
      {
        if (this._activeMode === "pan")
        this.initTracking(this._startXYZ.x, this._startXYZ.y);

        if (this._activeMode === "orbit")
        this.initOrbit();
      }
      !this._navapi.getSelectionSetsPivot() && this.utilities.pivotActive(this._navapi.getPivotSetFlag(), trigger === this._kWheel);

      if (this.utilities.autocam)
      {
        this.utilities.autocam.sync(this._camera);
        this.utilities.autocam.startInteraction(this._startXY.x, this._startXY.y);
        this._autoCamStartXY = this._startXY.clone();
      }
    }
  }

  interactionCheck()
  {
    // Restart keyboard interaction if certain keys are still down:
    //
    if (this._autoMove[0] ||
    this._autoMove[1] ||
    this._autoMove[2] ||
    this._autoMove[3] ||
    this._autoMove[4] ||
    this._autoMove[5] ||
    this._modifierState.SHIFT ||
    this._modifierState.CONTROL ||
    this._modifierState.ALT ||
    this._modifierState.SPACE) this.interactionStart(kKeyboard, true);
  }

  interactionEnd(trigger)
  {
    if (trigger === this._activeTrigger)
    {
      if (trigger !== this._kWheel)
      this.utilities.pivotActive(false);

      // We have to leave the _activeTrigger set until the
      // next update occurs so the update will apply the correct
      // operation.
      this._interactionActive = false;
    }
  }

  isInteractionActive() {
    return this._interactionActive;
  }

  // ------------------------
  // Event handler callbacks:
  // These can use "this".
  handleWheelInput(delta, event)
  {
    this._lastMouseX = event.canvasX;
    this._lastMouseY = event.canvasY;

    if (this.isDragging)
    return false;

    //Auto-update the center of zoom (pivot) to center on the cursor
    //on mouse wheel.
    if (this._navapi.getIs2D()) {
      // TODO: Perhaps this should be in the update method
      // to avoid unnecessary calls.
      var viewport = this._navapi.getScreenViewport();
      var point = this._viewerImpl.intersectGround(this._lastMouseX + viewport.width, this._lastMouseY + viewport.height);
      this.utilities.setPivotPoint(point, true, true);
    } else {
      this.setPivotPointFromInput(this._lastMouseX, this._lastMouseY);
    }

    if (this._navapi.getReverseZoomDirection())
    delta *= -1;

    this._moveXYZ.z += delta;

    if (delta != 0.0)
    this.interactionStart(this._kWheel);

    return true;
  }

  resetKeys()
  {
    // Turn off any auto motion that may be stuck due to lost focus
    this.autoMove(-1, false);

    // Clear modifier states:
    this._modifierState.SHIFT = 0;
    this._modifierState.CONTROL = 0;
    this._modifierState.ALT = 0;
    this._modifierState.SPACE = 0;
  }

  autoMove(index, state)
  {
    if (!state || !this.isDragging)
    {
      if (index < 0)
      this._autoMove[0] =
      this._autoMove[1] =
      this._autoMove[2] =
      this._autoMove[3] =
      this._autoMove[4] =
      this._autoMove[5] = state;else

      this._autoMove[index] = state;

      if (!state)
      this.interactionEnd(kKeyboard);

      this.interactionCheck();
    }
  }

  updateModifierState(event)
  {
    /* See SPK-930 and SPK-928
    _modifierState.CONTROL = ((isMac && event.metaKey) || (!isMac && event.ctrlKey)) ? 1 : 0;
     */
    this._modifierState.CONTROL = this._isMac && event.metaKey || event.ctrlKey ? 1 : 0;
    this._modifierState.SHIFT = event.shiftKey ? 1 : 0;
    this._modifierState.ALT = event.altKey ? 1 : 0;
  }

  handleKeyDown(event, keyCode)
  {
    this.updateModifierState(event);
    var handled = false;

    // On Mac KeyUp events are swallowed while CMD/Meta is pressed and there is no workaround
    // In that case we don't even allow movement to happen
    if (this._isMac && this._modifierState.CONTROL) {
      this.autoMove(-1, false);
      this.interactionEnd(kKeyboard); // Verify interaction didn't start again inside autoMove->interactionCheck
      return false;
    }

    switch (keyCode) {

      case _keys.EQUALS:this.adjustSpeed(1);handled = true;break;
      case _keys.DASH:this.adjustSpeed(-1);handled = true;break;
      case _keys.ZERO:this.adjustSpeed(0);handled = true;break; // Reset dolly speed to default

      case _keys.LEFT:this.autoMove(0, true);handled = true;break;
      case _keys.RIGHT:this.autoMove(1, true);handled = true;break;
      case _keys.PAGEUP:this.autoMove(2, true);handled = true;break;
      case _keys.PAGEDOWN:this.autoMove(3, true);handled = true;break;
      case _keys.UP:this.autoMove(4, true);handled = true;break;
      case _keys.DOWN:this.autoMove(5, true);handled = true;break;

      default:
        return false;
    }
    if (!this.isDragging)
    this.interactionStart(kKeyboard);

    return handled;
  }

  handleKeyUp(event, keyCode)
  {
    this.updateModifierState(event);
    var handled = false;

    // On Mac KeyUp events are swallowed while CMD/Meta is pressed and there is no workaround
    // We just stop interactions in that case
    if (this._isMac && this._modifierState.CONTROL) {
      this.autoMove(-1, false);
      this.interactionEnd(kKeyboard); // Verify interaction didn't start again inside autoMove->interactionCheck
      return false;
    }

    switch (keyCode) {

      case _keys.LEFT:this.autoMove(0, false);handled = true;break;
      case _keys.RIGHT:this.autoMove(1, false);handled = true;break;
      case _keys.PAGEUP:this.autoMove(2, false);handled = true;break;
      case _keys.PAGEDOWN:this.autoMove(3, false);handled = true;break;
      case _keys.UP:this.autoMove(4, false);handled = true;break;
      case _keys.DOWN:this.autoMove(5, false);handled = true;break;

      default:
        return false;
    }
    if (handled)
    {
      this.interactionEnd(kKeyboard);

      if (!this._interactionActive)
      this.interactionCheck();
    }
    return handled;
  }

  _endsWith(str, suffix) {
    return str.indexOf(suffix, str.length - suffix.length) !== -1;
  }

  _fingerSeparation(event) {
    var dx = event.pointers[1].clientX - event.pointers[0].clientX;
    var dy = event.pointers[1].clientY - event.pointers[0].clientY;
    return Math.sqrt(dx * dx + dy * dy);
  }

  handleDollyPan(event)
  {
    this._lastMouseX = event.canvasX;
    this._lastMouseY = event.canvasY;

    var viewport = this._navapi.getScreenViewport();
    this._moveXY.x = this._lastMouseX;
    this._moveXY.y = this._lastMouseY;
    this._moveXYZ.x = this._moveXY.x / viewport.width;
    this._moveXYZ.y = this._moveXY.y / viewport.height;

    this._pinchLength = this._fingerSeparation(event);

    var roll = THREE.Math.degToRad(event.rotation);
    this._deltaRoll = roll - this._prevRoll;
    if (Math.abs(this._deltaRoll) > 1.0)
    this._deltaRoll = 0;
    this._prevRoll = roll;

    if (this._endsWith(event.type, "start"))
    {
      this._prevPinchLength = this._pinchLength;
      this._prevPinchScale = 1.0;
      this._deltaRoll = 0;
      this._prevRoll = roll;
    }

    this._pinchScale = event.scale;
  }

  handleGesture(event)
  {
    switch (event.type) {

      case "dragstart":
        this._touchType = "drag";
        // Single touch, fake the mouse for now...
        return this.handleButtonDown(event, 0);

      case "dragmove":
        if (this._touchType !== "drag") {
          this.handleButtonDown(event, 0);
          this._touchType = "drag";
        }
        return this.handleMouseMove(event);

      case "dragend":
        // We seem to often get a lone dragend after a multi-touch.
        if (this._touchType === "drag")
        {
          this.handleButtonUp(event, 0);
          this._touchType = null;
          return true;
        }
        return false;


      case "panstart":
        this._touchType = "pan";
        this.handlePanStart(event);
        this.handleDollyPan(event);
        return true;

      case "panmove":
        if (this._touchType !== "pan") {
          this._touchType = "pan";
          this.handlePanStart(event);
        }
        return this.handleDollyPan(event);

      case "panend":
        if (this._touchType === "pan")
        {
          this.isDragging = false;
          this.handleDollyPan(event);
          this.interactionEnd(kTouch);
          return true;
        }
        return false;


      case "pinchstart":

        this.setPivotPointFromInput(event.center.x, event.center.y);

        this.isDragging = true;
        this._touchType = "pinch";

        this._startXYZ.x = (event.normalizedX + 1.0) * 0.5;
        this._startXYZ.y = 1.0 - (event.normalizedY + 1.0) * 0.5;

        this._touchStartXY.set(event.canvasX, event.canvasY);
        this._startXY.set(event.canvasX, event.canvasY);

        this._activeModeLocked = false;
        this.interactionStart(kTouch);
        this.handleDollyPan(event);
        return true;

      case "pinchmove":
        return this._touchType === "pinch" ? this.handleDollyPan(event) : false;

      case "pinchend":
        if (this._touchType === "pinch")
        {
          this.isDragging = false;
          this.handleDollyPan(event);
          this.interactionEnd(kTouch);
          return true;
        }
        return false;
    }
    return false;
  }

  handleButtonDown(event, button)
  {
    this.updateModifierState(event);

    this._startXYZ.x = (event.normalizedX + 1.0) * 0.5;
    this._startXYZ.y = 1.0 - (event.normalizedY + 1.0) * 0.5;

    this._startXY.set(event.canvasX, event.canvasY);
    this._moveXYZ.copy(this._startXYZ);
    this._moveXY.copy(this._startXY);

    this._rotateStart.copy(this._getMouseProjectionOnBall(event.canvasX, event.canvasY));
    this._rotateEnd.copy(this._rotateStart);

    this._lastMouseX = event.canvasX;
    this._lastMouseY = event.canvasY;

    this.isDragging = true;

    this.interactionStart(button);
    return true;
  }

  handlePanStart(event) {
    this.isDragging = true;

    this._startXYZ.x = (event.normalizedX + 1.0) * 0.5;
    this._startXYZ.y = 1.0 - (event.normalizedY + 1.0) * 0.5;

    this._touchStartXY.set(event.canvasX, event.canvasY);
    this._startXY.set(event.canvasX, event.canvasY);

    this.interactionStart(kTouch);
    return true;
  }

  handleButtonUp(event, button)
  {
    this.updateModifierState(event);

    this._moveXYZ.x = (event.normalizedX + 1.0) * 0.5;
    this._moveXYZ.y = 1.0 - (event.normalizedY + 1.0) * 0.5;
    this._moveXY.set(event.canvasX, event.canvasY);

    this._rotateEnd.copy(this._getMouseProjectionOnBall(event.canvasX, event.canvasY));
    this._rotateStart.copy(this._rotateEnd);

    this._lastMouseX = event.canvasX;
    this._lastMouseY = event.canvasY;

    this.interactionEnd(button);

    this.isDragging = false;
    return true;
  }

  handleBlur(event) {
    // Reset things when we lose focus...
    this.resetKeys();
    this.interactionEnd(this._activeTrigger);
  }

  handleMouseMove(event)
  {
    this.updateModifierState(event);

    //Handles non-dragging mouse move over the canvas.
    //Updates the last known mouse point for
    //using during mouse wheel (as zoom center) and
    //will eventually be needed for mouse over highlighting
    if (!this.isDragging)
    {
      this._startXYZ.x = (event.normalizedX + 1.0) * 0.5;
      this._startXYZ.y = 1.0 - (event.normalizedY + 1.0) * 0.5;

      this._startXY.set(event.canvasX, event.canvasY);
      this._moveXYZ.x = this._startXYZ.x;
      this._moveXYZ.y = this._startXYZ.y;
      this._moveXY.copy(this._startXY);

      this._lastMouseX = event.canvasX;
      this._lastMouseY = event.canvasY;

      // mouse over highlighting
      // TODO: Perhaps this should be in the update method
      // to avoid unnecessary calls.
      if (event.target === this._viewerImpl.canvas)
      {
        this._viewerImpl.rolloverObject(this._lastMouseX, this._lastMouseY);
      }

      return false;
    }
    this._moveXYZ.x = (event.normalizedX + 1.0) * 0.5;
    this._moveXYZ.y = 1.0 - (event.normalizedY + 1.0) * 0.5;
    this._moveXY.set(event.canvasX, event.canvasY);

    this._rotateEnd.copy(this._getMouseProjectionOnBall(event.canvasX, event.canvasY));

    this._lastMouseX = event.canvasX;
    this._lastMouseY = event.canvasY;

    return true;
  }

}